import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DateToLocale, ToFirstCapital } from "../../Utils/helpers";
import { viewLeadById, changeLeadStatus, listFranchiseByCity, assignFranchise, sendProjectDetailsWhatsapp } from "../../Redux/Actions/leadActions";
import MainLayout from "../../Layout/mainLayout";
import toast from "react-hot-toast";
import ViewLeadStatusHistory from "./viewLeadStatusHistory";
import "./viewLead.css";

const STATUS_OPTIONS = [
    { value: "pending", label: "Pending" },
    { value: "interested", label: "Interested" },
    { value: "not_interested", label: "Not Interested" },
    { value: "follow_up", label: "Follow Up" },
    { value: "missed_call", label: "Missed Call" },
    { value: "broker", label: "Broker" },
    { value: "completed", label: "Completed" },
];

const formatStatus = (s) => ToFirstCapital((s || "").replace(/_/g, " "));

const ViewLead = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const [leadData, setLeadData] = useState(null);
    const [pageLoading, setPageLoading] = useState(false);
    const [statusValue, setStatusValue] = useState("");
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [remark, setRemark] = useState("");
    const [showHistory, setShowHistory] = useState(false);
    const [sendingProjectId, setSendingProjectId] = useState(null);

    const [showAppointmentModal, setShowAppointmentModal] = useState(false);
    const [selectedFranchiseId, setSelectedFranchiseId] = useState("");
    const [franchiseLoading, setFranchiseLoading] = useState(false);
    const [assigning, setAssigning] = useState(false);
    const { franchiseList } = useSelector((state) => state.leads || {});

    const isSiteVisit = leadData?.lead_for === "Site_visit";

    // Returns YYYY-MM-DD in IST so date-only comparison is timezone-safe
    const toISTDateKey = (value) =>
        new Date(value).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

    const getScheduleDate = () => {
        if (isSiteVisit) return leadData?.site_visit?.pickup_date_time;
        return leadData?.user_appointment?.appointment_date;
    };

    // true when appointment date is today or in the future (or no date available)
    const isAppointmentDateValid = (() => {
        const scheduleDate = getScheduleDate();
        if (!scheduleDate) return true;
        return toISTDateKey(scheduleDate) >= toISTDateKey(new Date());
    })();

    const isAppointmentExpired = !isAppointmentDateValid;

    useEffect(() => {
        if (leadData?.status) {
            setStatusValue(leadData.status);
            setRemark(leadData?.remark || "");
        }
    }, [leadData]);

    const getStatusOptions = (currentStatus) => {
        if (currentStatus === "completed") return [];
        return STATUS_OPTIONS.filter((o) => o.value !== currentStatus);
    };

    const handleStatusSelect = (e) => {
        setStatusValue(e.target.value);
    };

    const handleStatusUpdate = async () => {
        if (!statusValue) return toast.error("Please select status");

        try {
            setStatusUpdating(true);
            const payload = { lead_id: id, status: statusValue, remark: remark.trim() || null };
            const res = await dispatch(changeLeadStatus(payload));
            if (res?.statusCode === 200) {
                setLeadData((prev) => ({
                    ...prev,
                    status: statusValue,
                    remark: payload.remark,
                    status_history: [
                        ...(prev.status_history || []),
                        { status: statusValue, remark: payload.remark, changed_at: new Date().toISOString() },
                    ],
                }));
                setRemark("");
            }
        } catch {
            toast.error("Something went wrong");
        } finally {
            setStatusUpdating(false);
        }
    };

    const openAppointmentModal = async () => {
        if (isAppointmentExpired) {
            return toast.error("Appointment date has already passed");
        }
        setShowAppointmentModal(true);
        setSelectedFranchiseId("");
        try {
            setFranchiseLoading(true);
            await dispatch(listFranchiseByCity({ city_id: leadData?.city?._id || leadData?.city, lead_id: id }));
        } catch {
            // handled by action toast
        } finally {
            setFranchiseLoading(false);
        }
    };

    const closeAppointmentModal = () => {
        setShowAppointmentModal(false);
        setSelectedFranchiseId("");
    };

    const handleAssignFranchise = async () => {
        if (isAppointmentExpired) {
            return toast.error("Appointment date has already passed");
        }
        if (!selectedFranchiseId) return toast.error("Please select a franchise");
        try {
            setAssigning(true);
            const res = await dispatch(assignFranchise({ lead_id: id, franchise_id: selectedFranchiseId }));
            if (res?.statusCode === 200) {
                const refreshed = await dispatch(viewLeadById(id));
                if (refreshed?.statusCode === 200) {
                    setLeadData(refreshed.data);
                }
                closeAppointmentModal();
            }
        } catch {
            toast.error("Something went wrong");
        } finally {
            setAssigning(false);
        }
    };

    const handleSendProjectDetails = async (projectId) => {
        if (!leadData?.user?._id) return toast.error("User not created yet for this lead");
        try {
            setSendingProjectId(projectId);
            const res = await dispatch(sendProjectDetailsWhatsapp({ project_id: projectId, user_id: leadData.user._id }));
            if (res?.statusCode === 200) {
                toast.success("Project details sent on WhatsApp");
            } else {
                toast.error(res?.message || "Failed to send project details");
            }
        } catch {
            toast.error("Something went wrong");
        } finally {
            setSendingProjectId(null);
        }
    };

    const formatIndianAmount = (value) => {
        if (value == null || isNaN(value)) return "";
        const num = Number(value);

        if (num >= 1_00_00_000) {
            return `${(num / 1_00_00_000).toFixed(num % 1_00_00_000 === 0 ? 0 : 2)} Cr`;
        }
        if (num >= 1_00_000) {
            return `${(num / 1_00_000).toFixed(num % 1_00_000 === 0 ? 0 : 2)} L`;
        }
        if (num >= 1_000) {
            return `${(num / 1_000).toFixed(num % 1_000 === 0 ? 0 : 2)} K`;
        }
        return num.toLocaleString("en-IN");
    };

    const formatDateTime = (value) => {
        if (!value) return "No Data";
        return new Date(value).toLocaleString('en-IN', {
            timeZone: 'Asia/Kolkata',
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    useEffect(() => {
        const fetchLead = async () => {
            try {
                setPageLoading(true);
                const res = await dispatch(viewLeadById(id));
                if (res && res.statusCode === 200) {
                    setLeadData(res.data);
                }
            } catch {
                // handle error
            } finally {
                setPageLoading(false);
            }
        };
        fetchLead();
    }, [id, dispatch]);

    if (pageLoading) {
        return (
            <MainLayout>
                <div className="vl-loader-wrap">
                    <div className="vl-loader" />
                    <p className="vl-loader-text">Loading lead data…</p>
                </div>
            </MainLayout>
        );
    }

    if (!leadData) {
        return (
            <MainLayout>
                <div className="vl-empty-wrap">
                    <div className="vl-empty-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 8v4M12 16h.01" />
                        </svg>
                    </div>
                    <p className="vl-empty-text">No Lead Data Found.</p>
                    <Link to="/leads" className="vl-back-link">← Back to Leads</Link>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="vl-wrapper">

                <div className="vl-header">
                    <div className="vl-header-left">
                        <Link to="/leads" className="vl-back-btn">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                            Back
                        </Link>
                        <div className="vl-header-divider" />
                        <div>
                            <div className="vl-breadcrumb">Leads / List / View</div>
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: "10px" }}>
                        <div className={`vl-status-badge vl-status-badge--${leadData?.status}`}>
                            {formatStatus(leadData?.status)}
                        </div>
                        {leadData?.status_history?.length > 0 && (
                            <button
                                className="vl-create-appt-btn"
                                onClick={() => setShowHistory(true)}
                                type="button"
                            >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <polyline points="12 6 12 12 16 14" />
                                </svg>
                                History
                            </button>
                        )}
                        {leadData?.lead_source === "partner" && leadData?.partner &&
                            leadData?.status !== "completed" && (
                                <Link to={`/leads/edit/${id}`} className="vl-create-appt-btn">
                                    Update Details
                                </Link>
                            )}
                        {leadData?.status === "interested" && (
                            <button
                                className="vl-create-appt-btn"
                                onClick={openAppointmentModal}
                                type="button"
                                disabled={!!leadData?.franchisy_id || isAppointmentExpired}
                                title={isAppointmentExpired ? "Appointment date has passed" : ""}
                            >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="4" width="18" height="18" rx="2" />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                    <line x1="12" y1="14" x2="12" y2="18" />
                                    <line x1="10" y1="16" x2="14" y2="16" />
                                </svg>
                                {leadData?.franchisy_id
                                    ? "Franchise Assigned"
                                    : isAppointmentExpired
                                        ? "Appointment Date Passed"
                                        : "Assign Franchise"}
                            </button>
                        )}
                    </div>
                </div>

                <div className="vl-hero-card">
                    <div className="vl-hero-left">
                        <div className="vl-hero-avatar">
                            {leadData?.name?.charAt(0)?.toUpperCase() || "L"}
                        </div>
                        <div>
                            <div className="vl-hero-label">Lead Name</div>
                            <div className="vl-hero-value">{leadData?.name || "—"}</div>
                            {leadData?.email && (
                                <div className="vl-hero-sub">{leadData.email}</div>
                            )}
                        </div>
                    </div>
                    <div className="vl-hero-meta">
                        {(leadData?.country_code || leadData?.contact_number) && (
                            <div className="vl-hero-meta-item">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.36 12 19.79 19.79 0 0 1 1.21 3.58 2 2 0 0 1 3.22 1.4h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16.92z" />
                                </svg>
                                {leadData?.country_code} {leadData?.contact_number}
                            </div>
                        )}
                        {leadData?.country?.country_name && (
                            <div className="vl-hero-meta-item">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="2" y1="12" x2="22" y2="12" />
                                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                                </svg>
                                {leadData.country.country_name}
                            </div>
                        )}
                        <div className="vl-hero-meta-item">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                            {DateToLocale(leadData?.created_at) || "—"}
                        </div>
                    </div>
                </div>

                <div className="vl-grid-layout">

                    <div className="vl-col">

                        <div className="vl-card">
                            <div className="vl-card-header">
                                <span className="vl-card-badge">01</span>
                                <h2 className="vl-card-title">Lead Information</h2>
                            </div>
                            <div className="vl-info-grid">
                                <div className="vl-info-item">
                                    <span className="vl-info-label">Full Name</span>
                                    <span className="vl-info-value">{leadData?.name || "No Data"}</span>
                                </div>
                                <div className="vl-info-item">
                                    <span className="vl-info-label">Email</span>
                                    <span className="vl-info-value">{leadData?.email || "No Data"}</span>
                                </div>
                                <div className="vl-info-item">
                                    <span className="vl-info-label">Contact Number</span>
                                    <span className="vl-info-value">
                                        {leadData?.country_code && leadData?.contact_number
                                            ? `${leadData.country_code} ${leadData.contact_number}`
                                            : leadData?.contact_number || "No Data"}
                                    </span>
                                </div>
                                <div className="vl-info-item">
                                    <span className="vl-info-label">Status</span>
                                    <span className={`vl-inline-status vl-inline-status--${leadData?.status}`}>
                                        {formatStatus(leadData?.status)}
                                    </span>
                                </div>
                                <div className="vl-info-item vl-info-item--full">
                                    <span className="vl-info-label">Remark</span>
                                    <span className="vl-info-value">{leadData?.remark || "No Data"}</span>
                                </div>
                                <div className="vl-info-item">
                                    <span className="vl-info-label">Country</span>
                                    <span className="vl-info-value">{leadData?.country?.country_name || "No Data"}</span>
                                </div>
                                <div className="vl-info-item">
                                    <span className="vl-info-label">Currency</span>
                                    <span className="vl-info-value">{leadData?.country?.country_currency || "No Data"}</span>
                                </div>
                                <div className="vl-info-item">
                                    <span className="vl-info-label">City</span>
                                    <span className="vl-info-value">{leadData?.city?.city_name || "No Data"}</span>
                                </div>
                                <div className="vl-info-item">
                                    <span className="vl-info-label">Locality</span>
                                    <span className="vl-info-value">
                                        {leadData?.locality?.name || "No Data"}
                                        {/* {leadData?.locality?.is_major && (
                                            <span className="vl-major-badge">Major</span>
                                        )} */}
                                    </span>
                                    {leadData?.locality?.short_address && (
                                        <span className="vl-info-sub">{leadData.locality.short_address}</span>
                                    )}
                                </div>
                                <div className="vl-info-item">
                                    <span className="vl-info-label">Lead Source</span>
                                    <span className="vl-info-value">
                                        {leadData?.lead_source ? ToFirstCapital(leadData.lead_source.replace(/_/g, " ")) : "No Data"}
                                    </span>
                                </div>
                                <div className="vl-info-item">
                                    <span className="vl-info-label">Lead For</span>
                                    <span className="vl-info-value">
                                        {leadData?.lead_for ? ToFirstCapital(leadData.lead_for.replace(/_/g, " ")) : "No Data"}
                                    </span>
                                </div>
                                <div className="vl-info-item vl-info-item--full">
                                    <span className="vl-info-label">Price Range</span>
                                    <span className="vl-info-value vl-price-range-value">
                                        {typeof leadData?.price_range === "object" && leadData.price_range
                                            ? `${ToFirstCapital(leadData.price_range.category || "")}${leadData.price_range.min_price != null && leadData.price_range.max_price != null
                                                ? ` (${leadData.price_range.currency || ""} ${formatIndianAmount(
                                                    leadData.price_range.min_price
                                                )} - ${formatIndianAmount(leadData.price_range.max_price)})`
                                                : ""
                                            }`
                                            : leadData?.price_range
                                                ? "Assigned"
                                                : "No Data"}
                                    </span>
                                </div>
                                <div className="vl-info-item">
                                    <span className="vl-info-label">Created At</span>
                                    <span className="vl-info-value">{DateToLocale(leadData?.created_at) || "No Data"}</span>
                                </div>
                            </div>
                        </div>

                        {Array.isArray(leadData?.project_id) && leadData.project_id.length > 0 && (
                            <div className="vl-card">
                                <div className="vl-card-header">
                                    <span className="vl-card-badge">09</span>
                                    <h2 className="vl-card-title">Project(s)</h2>
                                </div>
                                <div className="vl-notes-list">
                                    {leadData.project_id.map((proj) => (
                                        <div key={proj._id || proj} className="vl-address-item">
                                            {proj.cover_photo_thumbnail && (
                                                <img
                                                    src={proj.cover_photo_thumbnail}
                                                    alt={proj.project_name}
                                                    style={{
                                                        width: 44,
                                                        height: 44,
                                                        borderRadius: 8,
                                                        objectFit: "cover",
                                                        flexShrink: 0,
                                                    }}
                                                />
                                            )}
                                            <div>
                                                <div className="vl-address-label">
                                                    {proj.business_type ? ToFirstCapital(proj.business_type) : ""}
                                                </div>
                                                <div className="vl-address-val">
                                                    {typeof proj === "object" ? (proj.project_name || "No Data") : proj}
                                                </div>
                                                {proj.street_address && (
                                                    <div className="vl-address-val" style={{ fontWeight: 400, fontSize: 12.5, color: "var(--vl-text-secondary)" }}>
                                                        {proj.street_address}
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                className="vl-wa-btn"
                                                onClick={() => handleSendProjectDetails(proj._id || proj)}
                                                disabled={sendingProjectId === (proj._id || proj) || !leadData?.user?._id}
                                                title={leadData?.user?._id ? "Send project details on WhatsApp" : "User not created yet"}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {leadData?.address && (leadData.address.address || leadData.address.appartment_road_area || leadData.address.house_flat_floorno || leadData.address.business_type || leadData.address.direction_to_reach) && (
                            <div className="vl-card">
                                <div className="vl-card-header">
                                    <span className="vl-card-badge">04</span>
                                    <h2 className="vl-card-title">{isSiteVisit ? "Pickup Address" : "Address"}</h2>
                                </div>
                                <div className="vl-address-item">
                                    <div className="vl-address-icon">
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
                                            <circle cx="12" cy="10" r="3" />
                                        </svg>
                                    </div>
                                    <div>
                                        <div className="vl-address-label">Full Address</div>
                                        <div className="vl-address-val">{leadData.address.address || "No Data"}</div>
                                    </div>
                                </div>

                                {leadData.address.house_flat_floorno && (
                                    <div className="vl-address-item">
                                        <div className="vl-address-icon">
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                                <path d="M9 22V12h6v10" />
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="vl-address-label">House / Flat / Floor No.</div>
                                            <div className="vl-address-val">{leadData.address.house_flat_floorno}</div>
                                        </div>
                                    </div>
                                )}

                                {leadData.address.appartment_road_area && (
                                    <div className="vl-address-item">
                                        <div className="vl-address-icon">
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M3 12h18M3 6h18M3 18h18" />
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="vl-address-label">Apartment / Road / Area</div>
                                            <div className="vl-address-val">{leadData.address.appartment_road_area}</div>
                                        </div>
                                    </div>
                                )}

                                {leadData.address.business_type && (
                                    <div className="vl-address-item">
                                        <div className="vl-address-icon">
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <rect x="3" y="7" width="18" height="13" rx="2" />
                                                <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="vl-address-label">Business Type</div>
                                            <div className="vl-address-val">{leadData.address.business_type}</div>
                                        </div>
                                    </div>
                                )}

                                {leadData.address.direction_to_reach && (
                                    <div className="vl-address-item">
                                        <div className="vl-address-icon">
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M3 11l19-9-9 19-2-8-8-2z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="vl-address-label">Direction to Reach</div>
                                            <div className="vl-address-val">{leadData.address.direction_to_reach}</div>
                                        </div>
                                    </div>
                                )}

                                {isSiteVisit && leadData?.drop_address && (
                                    <div className="vl-address-item">
                                        <div className="vl-address-icon">
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M12 2L2 22h20L12 2z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="vl-address-label">Drop Address</div>
                                            <div className="vl-address-val">{leadData.drop_address}</div>
                                        </div>
                                    </div>
                                )}

                                {isSiteVisit && leadData?.site_visit_purpose && (
                                    <div className="vl-address-item">
                                        <div className="vl-address-icon">
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <circle cx="12" cy="12" r="10" />
                                                <path d="M12 16v-4M12 8h.01" />
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="vl-address-label">Site Visit Purpose</div>
                                            <div className="vl-address-val">{leadData.site_visit_purpose}</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {leadData?.location?.coordinates?.length === 2 &&
                            (leadData.location.coordinates[0] !== 0 || leadData.location.coordinates[1] !== 0) && (
                                <div className="vl-card">
                                    <div className="vl-card-header">
                                        <span className="vl-card-badge">05</span>
                                        <h2 className="vl-card-title">{isSiteVisit ? "Pickup Location" : "Location"}</h2>
                                    </div>
                                    <div className="vl-coords-row" style={{ marginBottom: 12 }}>
                                        <div className="vl-coord-chip">
                                            <span className="vl-coord-label">LAT</span>
                                            <span className="vl-coord-value">
                                                {leadData.location.coordinates[1].toFixed(6)}
                                            </span>
                                        </div>
                                        <div className="vl-coord-chip">
                                            <span className="vl-coord-label">LNG</span>
                                            <span className="vl-coord-value">
                                                {leadData.location.coordinates[0].toFixed(6)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="vl-map-wrap">
                                        <iframe
                                            title="Lead Location"
                                            className="vl-map-iframe"
                                            loading="lazy"
                                            allowFullScreen
                                            src={`https://maps.google.com/maps?q=${leadData.location.coordinates[1]},${leadData.location.coordinates[0]}&z=15&output=embed`}
                                        />
                                    </div>
                                </div>
                            )}

                        {isSiteVisit && leadData?.drop_location?.coordinates?.length === 2 &&
                            (leadData.drop_location.coordinates[0] !== 0 || leadData.drop_location.coordinates[1] !== 0) && (
                                <div className="vl-card">
                                    <div className="vl-card-header">
                                        <span className="vl-card-badge">10</span>
                                        <h2 className="vl-card-title">Drop Location</h2>
                                    </div>
                                    <div className="vl-coords-row" style={{ marginBottom: 12 }}>
                                        <div className="vl-coord-chip">
                                            <span className="vl-coord-label">LAT</span>
                                            <span className="vl-coord-value">
                                                {leadData.drop_location.coordinates[1].toFixed(6)}
                                            </span>
                                        </div>
                                        <div className="vl-coord-chip">
                                            <span className="vl-coord-label">LNG</span>
                                            <span className="vl-coord-value">
                                                {leadData.drop_location.coordinates[0].toFixed(6)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="vl-map-wrap">
                                        <iframe
                                            title="Drop Location"
                                            className="vl-map-iframe"
                                            loading="lazy"
                                            allowFullScreen
                                            src={`https://maps.google.com/maps?q=${leadData.drop_location.coordinates[1]},${leadData.drop_location.coordinates[0]}&z=15&output=embed`}
                                        />
                                    </div>
                                </div>
                            )}

                    </div>

                    <div className="vl-col">

                        {leadData?.partner && (
                            <div className="vl-card">
                                <div className="vl-card-header">
                                    <span className="vl-card-badge">03</span>
                                    <h2 className="vl-card-title">Partner Details</h2>
                                </div>
                                <div className="vl-info-grid">
                                    {leadData.partner.profile_image && (
                                        <div className="vl-info-item" style={{ gridColumn: "1 / -1" }}>
                                            <img
                                                src={leadData.partner.profile_image}
                                                alt={leadData.partner.name}
                                                style={{
                                                    width: 64,
                                                    height: 64,
                                                    borderRadius: "50%",
                                                    objectFit: "cover",
                                                    border: "2px solid #e5e7eb",
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div className="vl-info-item">
                                        <span className="vl-info-label">Full Name</span>
                                        <span className="vl-info-value">{leadData.partner?.name || "No Data"}</span>
                                    </div>
                                    <div className="vl-info-item">
                                        <span className="vl-info-label">Staff ID</span>
                                        <span className="vl-info-value">{leadData.partner?.staff_id || "No Data"}</span>
                                    </div>
                                    <div className="vl-info-item">
                                        <span className="vl-info-label">Email</span>
                                        <span className="vl-info-value">{leadData.partner?.email || "No Data"}</span>
                                    </div>
                                    <div className="vl-info-item">
                                        <span className="vl-info-label">Contact Number</span>
                                        <span className="vl-info-value">{leadData.partner?.contact_number || "No Data"}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {leadData?.note?.length > 0 && (
                            <div className="vl-card">
                                <div className="vl-card-header">
                                    <span className="vl-card-badge">06</span>
                                    <h2 className="vl-card-title">Notes</h2>
                                </div>
                                <div className="vl-notes-list">
                                    {leadData.note.map((n, idx) => (
                                        <div key={idx} className="vl-note-item">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                            </svg>
                                            <span>{n}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}


                        {leadData?.reason && (
                            <div className="vl-card">
                                <div className="vl-card-header">
                                    <span className="vl-card-badge">02</span>
                                    <h2 className="vl-card-title">Reason</h2>
                                </div>
                                <p className="vl-reason-text">{leadData.reason}</p>
                            </div>
                        )}

                        {leadData?.franchisy_id && typeof leadData.franchisy_id === "object" && (
                            <div className="vl-card">
                                <div className="vl-card-header">
                                    <span className="vl-card-badge">07</span>
                                    <h2 className="vl-card-title">Franchise Details</h2>
                                </div>
                                <div className="vl-info-grid">
                                    {leadData.franchisy_id.profile_image && (
                                        <div className="vl-info-item" style={{ gridColumn: "1 / -1" }}>
                                            <img
                                                src={leadData.franchisy_id.profile_image}
                                                alt={leadData.franchisy_id.name}
                                                style={{
                                                    width: 64,
                                                    height: 64,
                                                    borderRadius: "50%",
                                                    objectFit: "cover",
                                                    border: "2px solid #e5e7eb",
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div className="vl-info-item">
                                        <span className="vl-info-label">Franchise Name</span>
                                        <span className="vl-info-value">{leadData.franchisy_id?.name || "No Data"}</span>
                                    </div>
                                    <div className="vl-info-item">
                                        <span className="vl-info-label">Role</span>
                                        <span className="vl-info-value">
                                            {leadData.franchisy_id?.role ? ToFirstCapital(leadData.franchisy_id.role.replace(/_/g, " ")) : "No Data"}
                                        </span>
                                    </div>
                                    <div className="vl-info-item">
                                        <span className="vl-info-label">Email</span>
                                        <span className="vl-info-value">{leadData.franchisy_id?.email || "No Data"}</span>
                                    </div>
                                    <div className="vl-info-item">
                                        <span className="vl-info-label">Contact Number</span>
                                        <span className="vl-info-value">{leadData.franchisy_id?.contact_number || "No Data"}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {!isSiteVisit && leadData?.user_appointment && typeof leadData.user_appointment === "object" && (
                            <div className="vl-card">
                                <div className="vl-card-header">
                                    <span className="vl-card-badge">08</span>
                                    <h2 className="vl-card-title">Appointment & Payment</h2>
                                </div>
                                <div className="vl-info-grid">
                                    <div className="vl-info-item">
                                        <span className="vl-info-label">Appointment Date</span>
                                        <span className="vl-info-value">
                                            {leadData.user_appointment.appointment_date
                                                ? new Date(leadData.user_appointment.appointment_date).toLocaleDateString('en-IN', {
                                                    timeZone: 'Asia/Kolkata',
                                                    day: '2-digit',
                                                    month: 'short',
                                                    year: 'numeric',
                                                })
                                                : "No Data"}
                                        </span>
                                    </div>
                                    <div className="vl-info-item">
                                        <span className="vl-info-label">Time Slot</span>
                                        <span className="vl-info-value">{leadData.user_appointment.time_slot || "No Data"}</span>
                                    </div>
                                    <div className="vl-info-item">
                                        <span className="vl-info-label">Appointment Status</span>
                                        <span className={`vl-inline-status vl-inline-status--${leadData.user_appointment.status?.toLowerCase()}`}>
                                            {leadData.user_appointment.status ? ToFirstCapital(leadData.user_appointment.status) : "No Data"}
                                        </span>
                                    </div>
                                    <div className="vl-info-item">
                                        <span className="vl-info-label">Payment Status</span>
                                        <span className={`vl-inline-status vl-inline-status--${leadData.user_appointment.payment_status?.toLowerCase()}`}>
                                            {leadData.user_appointment.payment_status ? ToFirstCapital(leadData.user_appointment.payment_status) : "No Data"}
                                        </span>
                                    </div>
                                    <div className="vl-info-item">
                                        <span className="vl-info-label">Booking Amount</span>
                                        <span className="vl-info-value">
                                            {leadData.user_appointment.booking_amount != null
                                                ? leadData.user_appointment.booking_amount === 0
                                                    ? <span className="vl-free-badge">Free</span>
                                                    : `₹ ${formatIndianAmount(leadData.user_appointment.booking_amount)}`
                                                : "No Data"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {isSiteVisit && leadData?.site_visit && typeof leadData.site_visit === "object" && (
                            <div className="vl-card">
                                <div className="vl-card-header">
                                    <span className="vl-card-badge">08</span>
                                    <h2 className="vl-card-title">Site Visit Details</h2>
                                </div>
                                <div className="vl-info-grid">
                                    <div className="vl-info-item">
                                        <span className="vl-info-label">Visit By</span>
                                        <span className="vl-info-value">
                                            {leadData.site_visit.site_visit_by ? ToFirstCapital(leadData.site_visit.site_visit_by) : "No Data"}
                                        </span>
                                    </div>
                                    <div className="vl-info-item">
                                        <span className="vl-info-label">Visit Status</span>
                                        <span className={`vl-inline-status vl-inline-status--${leadData.site_visit.status?.toLowerCase()}`}>
                                            {leadData.site_visit.status ? ToFirstCapital(leadData.site_visit.status) : "No Data"}
                                        </span>
                                    </div>
                                    <div className="vl-info-item">
                                        <span className="vl-info-label">Pickup Date & Time</span>
                                        <span className="vl-info-value">
                                            {formatDateTime(leadData.site_visit.pickup_date_time)}
                                        </span>
                                    </div>
                                    {leadData.site_visit.otp && (
                                        <div className="vl-info-item">
                                            <span className="vl-info-label">OTP</span>
                                            <span className="vl-info-value">{leadData.site_visit.otp}</span>
                                        </div>
                                    )}
                                    {leadData.site_visit.agent && typeof leadData.site_visit.agent === "object" && (
                                        <>
                                            <div className="vl-info-item">
                                                <span className="vl-info-label">Agent Name</span>
                                                <span className="vl-info-value">{leadData.site_visit.agent.name || "No Data"}</span>
                                            </div>
                                            <div className="vl-info-item">
                                                <span className="vl-info-label">Agent Contact</span>
                                                <span className="vl-info-value">{leadData.site_visit.agent.contact_number || "No Data"}</span>
                                            </div>
                                        </>
                                    )}
                                    <div className="vl-info-item vl-info-item--full">
                                        <span className="vl-info-label">Pickup Address</span>
                                        <span className="vl-info-value">{leadData.site_visit.pickup_address || "No Data"}</span>
                                    </div>
                                    <div className="vl-info-item vl-info-item--full">
                                        <span className="vl-info-label">Drop Address</span>
                                        <span className="vl-info-value">{leadData.site_visit.drop_address || "No Data"}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {leadData?.status !== "completed" && (
                            <div className="vl-card">
                                <div className="vl-card-header">
                                    <span className="vl-card-badge">02</span>
                                    <h2 className="vl-card-title">Update Status</h2>
                                </div>

                                <div className="vl-status-panel">
                                    <div className="vl-status-field">
                                        <label className="vl-info-label">Select Status</label>
                                        <select
                                            className="vl-status-select"
                                            value={statusValue}
                                            onChange={handleStatusSelect}
                                            disabled={statusUpdating}
                                        >
                                            <option value={leadData?.status}>{formatStatus(leadData?.status)}</option>
                                            {getStatusOptions(leadData?.status).map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="vl-status-field">
                                        <label className="vl-info-label">Remark</label>
                                        <textarea
                                            className="vl-status-textarea"
                                            value={remark}
                                            onChange={(e) => setRemark(e.target.value)}
                                            placeholder="Add a remark for this status change..."
                                            rows={3}
                                            disabled={statusUpdating}
                                        />
                                    </div>

                                    <button
                                        type="button"
                                        className="vl-status-btn"
                                        onClick={handleStatusUpdate}
                                        disabled={statusUpdating || !statusValue || statusValue === leadData?.status}
                                    >
                                        {statusUpdating ? "Updating..." : "Update Status"}
                                    </button>
                                </div>
                            </div>
                        )}

                    </div>
                </div>

                {showAppointmentModal && (
                    <div className="vl-modal-overlay" onClick={closeAppointmentModal}>
                        <div className="vl-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="vl-modal-header">
                                <h3 className="vl-modal-title">Assign Franchise</h3>
                                <button className="vl-modal-close" onClick={closeAppointmentModal} type="button">×</button>
                            </div>

                            <div className="vl-modal-body">
                                {franchiseLoading ? (
                                    <div className="vl-modal-loading">Loading franchises...</div>
                                ) : franchiseList?.data?.length > 0 ? (
                                    <div className="vl-franchise-list">
                                        {franchiseList.data.map((f) => (
                                            <label
                                                key={f._id}
                                                className={`vl-franchise-item ${selectedFranchiseId === f._id ? "vl-franchise-item--selected" : ""}`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="franchise"
                                                    value={f._id}
                                                    checked={selectedFranchiseId === f._id}
                                                    onChange={() => setSelectedFranchiseId(f._id)}
                                                />
                                                <div className="vl-franchise-info">
                                                    <span className="vl-franchise-name">{f.name || "Unnamed"}</span>
                                                    <span className="vl-franchise-meta">
                                                        {f.role ? ToFirstCapital(f.role.replace(/_/g, " ")) : ""}
                                                    </span>
                                                    <span className="vl-franchise-meta">{f.email || f.contact_number || ""}</span>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="vl-modal-empty">No franchises found for this city.</div>
                                )}
                            </div>

                            <div className="vl-modal-footer">
                                <button className="vl-btn-cancel" onClick={closeAppointmentModal} type="button" disabled={assigning}>
                                    Cancel
                                </button>
                                <button
                                    className="vl-status-btn"
                                    onClick={handleAssignFranchise}
                                    type="button"
                                    disabled={assigning || !selectedFranchiseId}
                                >
                                    {assigning ? "Assigning..." : "Assign & Create Appointment"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <ViewLeadStatusHistory
                    open={showHistory}
                    onClose={() => setShowHistory(false)}
                    history={leadData?.status_history}
                />
            </div>
        </MainLayout>
    );
};

export default ViewLead;